import * as _ from 'lodash';

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions/selectOption.interface';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
})
export class MultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public options$: BehaviorSubject<ISelectOption[]>;
  @Input() public selectedIds: string[];
  @Input() public valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  @Input() public disabled = false;
  @Input() public placeHolder = '';
  @Input() public withSearch = false;
  @Input() public withSelectAll = false;
  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public selectedIdsSet: Set<string> = new Set();
  public categoriesSet: Set<string> = new Set();
  public optionsVisible = false;
  public optionsById: Map<string, ISelectOption> = new Map<string, ISelectOption>();
  public filteredOptions$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>([]);
  public searchValues: string[] = [];

  public isFormValid = true;
  public allChecked = false;

  private _optionsSubscription?: Subscription;

  ngOnInit(): void {
    this.selectedIdsSet = new Set(this.selectedIds);
    this._optionsSubscription = this.options$.subscribe((options) => {
      this.searchValues = options.map((option) => option.displayName);
      this.filteredOptions$.next(this._initializeOptions(options.sort((a, b) => this._sortSelectedOptions(a, b))));

      if (options.length > 0) {
        this._handelSelectedOptionAfterOptionChange(this.options$.value);
      }
    });
    this.valid.subscribe((value) => (this.isFormValid = value));
    this._setAllChecked();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('selectedIds' in changes && changes['selectedIds'].previousValue !== changes['selectedIds'].currentValue) {
      this._handleSelectedIdOnChange(changes['selectedIds'].currentValue as string[]);
    }
  }

  ngOnDestroy(): void {
    this._optionsSubscription?.unsubscribe;
  }

  public clickOutsideMultiSelect(): void {
    if (this.optionsVisible === true) {
      this.optionsVisible = false;
      this.optionOnSelection.emit(this._getSelectedOptions());
      this.filteredOptions$.next(
        this._initializeOptions(this.options$.value.sort((a, b) => this._sortSelectedOptions(a, b)))
      );
      this._setAllChecked();
    }
  }

  public onSelect(id: string): void {
    if (!this.selectedIdsSet.has(id)) {
      this.selectedIdsSet.add(id);
    } else {
      this.selectedIdsSet.delete(id);
    }

    this._setAllChecked();
    // Ensure selected counts are updated after selection
    this._updateSelectedCounts();
  }

  public onSelectAll(): void {
    const selectionOptionIds: Set<string> = new Set();
    // Iterate over each category and option, adding option ids to selectionOptionIds
    this.filteredOptions$.value.forEach((category) => {
      category.options.forEach((option) => {
        selectionOptionIds.add(option.id);
      });
    });

    if (this.allChecked) {
      this.selectedIdsSet = new Set([...this.selectedIdsSet].filter((x) => !new Set(selectionOptionIds).has(x)));
    } else {
      this.selectedIdsSet = new Set([...this.selectedIdsSet, ...selectionOptionIds]);
      this.filteredOptions$.value.forEach((category) => {
        category.options.forEach((option) => {
          selectionOptionIds.add(option.id);
        });
      });
    }
    this._setAllChecked();
    this._updateSelectedCounts();
  }

  public clearSelections(): void {
    this.selectedIdsSet = new Set<string>();
    this._setAllChecked();
    this._updateSelectedCounts();

    const selectedOptions = this._getSelectedOptions();
    if (this.optionsVisible == false) {
      this.optionOnSelection.emit(selectedOptions);
    }
  }

  public onSearch(values: string[]): void {
    if (values.length != this.options$.value.length) {
      let options = this.options$.value;
      const loweredValues = values.map((value) => value.toLowerCase());
      options = options
        .filter((option) => loweredValues.indexOf(option.displayName.toLowerCase()) > -1)
        .sort((a, b) => this._sortSelectedOptions(a, b));

      this.filteredOptions$.next(this._initializeOptions(options));
    } else {
      this.filteredOptions$.next(
        this._initializeOptions(this.options$.value.sort((a, b) => this._sortSelectedOptions(a, b)))
      );
    }
    this._setAllChecked();
  }

  public isGroupSelected(group: ISelectOptionGroups): boolean {
    return group.options.some((option) => this.selectedIdsSet.has(option.id));
  }

  // Method to update selected counts for each category
  private _updateSelectedCounts(): void {
    this.filteredOptions$.value.forEach((category) => {
      // Count the selected options in each category and assign it to selectedCount
      category.selectedCount = category.options.filter((option) => this.selectedIdsSet.has(option.id)).length;
    });
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIdsSet);
    const optionsById = _.keyBy(this.options$.value, 'id');

    return selectedIndexArray.map((id: string) => optionsById[id]);
  }

  private _handleSelectedIdOnChange(newIds: string[]) {
    if (this.options$.value.length > 0) {
      if (!(newIds.length == this.selectedIdsSet.size && newIds.every((id) => this.selectedIdsSet.has(id)))) {
        this.selectedIdsSet = new Set<string>(newIds);
        this._setAllChecked();
        this.optionOnSelection.emit(this._getSelectedOptions());
      }
    }
  }

  private _initializeOptions(options: ISelectOption[]): ISelectOptionGroups[] {
    const filteredOptions: ISelectOptionGroups[] = [];
    const indexByCategory: { [key: string]: number } = {};

    options.forEach((option) => {
      if (option.categoryName) {
        if (option.categoryName in indexByCategory) {
          // Add option to existing category
          const category = filteredOptions[indexByCategory[option.categoryName]];
          category.options.push(option);

          // Increment selectedCount if this option is selected
          if (this.selectedIdsSet.has(option.id)) {
            category.selectedCount += 1;
          }
        } else {
          // Create new category with initial selectedCount based on this option
          const isSelected = this.selectedIdsSet.has(option.id) ? 1 : 0;
          filteredOptions.push({
            selectedCount: isSelected,
            displayName: option.categoryName,
            options: [option],
          });

          // Record the category index for future options in this category
          indexByCategory[option.categoryName] = filteredOptions.length - 1;
        }
      } else {
        // For options without a category, add them as standalone entries
        filteredOptions.push({
          options: [option],
        } as ISelectOptionGroups);
      }
    });

    return filteredOptions;
  }

  private _handelSelectedOptionAfterOptionChange(newOptions: ISelectOption[]) {
    this.optionsById = new Map<string, ISelectOption>(newOptions.map((option) => [option.id, option]));
    const foundIds = new Set<string>();

    this.selectedIds.forEach((id) => {
      if (this.optionsById.has(id)) {
        foundIds.add(id);
      }
    });

    if (!(foundIds.size == this.selectedIdsSet.size && [...foundIds].every((id) => this.selectedIdsSet.has(id)))) {
      this.selectedIdsSet = foundIds;
      this.optionOnSelection.emit(this._getSelectedOptions());
    }
    this._setAllChecked();
  }

  private _setAllChecked() {
    const newSelectedIds: Set<string> = new Set();

    this.filteredOptions$.value.forEach((category) => {
      category.options.forEach((option) => {
        newSelectedIds.add(option.id);
      });
    });

    if (this.selectedIdsSet.size < newSelectedIds.size) {
      this.allChecked = false;
    }

    this.allChecked = Array.from(newSelectedIds).every((element) => this.selectedIdsSet.has(element));
  }

  private _sortSelectedOptions(a: ISelectOption, b: ISelectOption) {
    if (this.selectedIdsSet.has(a.id) && this.selectedIdsSet.has(b.id)) {
      return 0;
    }
    if (this.selectedIdsSet.has(a.id) && !this.selectedIdsSet.has(b.id)) {
      return -1;
    }
    return 1;
  }
}
